import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { COMPLETE_GSC_OAUTH } from '../../graphql/mutations';
import { GscOAuthResult } from '../../types';

const client = generateClient();

export function GscCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Completing Google Search Console connection...');

  useEffect(() => {
    const completeOAuth = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      // Check for OAuth error from Google
      if (error) {
        setStatus('error');
        setMessage(`Google returned an error: ${error}. Please try again.`);
        return;
      }

      // Validate required parameters
      if (!code || !state) {
        setStatus('error');
        setMessage('Missing required OAuth parameters. Please try again.');
        return;
      }

      // Retrieve stored state and siteId
      const storedState = sessionStorage.getItem('gsc_oauth_state');
      const siteId = sessionStorage.getItem('gsc_oauth_siteId');

      // Validate state matches
      if (state !== storedState) {
        setStatus('error');
        setMessage('OAuth state mismatch. This may be a security issue. Please try again.');
        return;
      }

      if (!siteId) {
        setStatus('error');
        setMessage('Site ID not found. Please try connecting again.');
        return;
      }

      try {
        const response = await client.graphql({
          query: COMPLETE_GSC_OAUTH,
          variables: {
            siteId,
            code,
            state,
          },
        }) as { data: { completeGscOAuth: GscOAuthResult } };

        const result = response.data.completeGscOAuth;

        // Clean up session storage
        sessionStorage.removeItem('gsc_oauth_state');
        sessionStorage.removeItem('gsc_oauth_siteId');

        if (result.success) {
          setStatus('success');
          setMessage(`Successfully connected to Google Search Console! Property: ${result.propertyUrl}`);

          // Redirect back to site settings after 2 seconds
          setTimeout(() => {
            navigate(`/sites/${siteId}`);
          }, 2000);
        } else {
          setStatus('error');
          setMessage(result.message);
        }
      } catch (err) {
        console.error('Error completing GSC OAuth:', err);
        setStatus('error');
        setMessage('Failed to complete the connection. Please try again.');
      }
    };

    completeOAuth();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 size={48} className="mx-auto text-coral animate-spin mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Connecting Google Search Console
            </h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={48} className="mx-auto text-green-600 mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Connection Successful!
            </h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">Redirecting you back to site settings...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle size={48} className="mx-auto text-red-600 mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Connection Failed
            </h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary"
            >
              Return to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
